import { ExpandMore } from '@mui/icons-material'
import { IconButton, Paper, Box, Stack, useTheme } from '@mui/material'
import { useState } from 'react'
import { useLocation } from 'react-router-dom'

import LoadingPaperSkeleton from '~/components/loadingPaperSkeleton'
import AgentChatInfo from '~/features/agents/components/agentInfoHeader/agentChatInfo'
import AgentEmailInfo from '~/features/agents/components/agentInfoHeader/agentEmailInfo'
import AgentNameInfo from '~/features/agents/components/agentInfoHeader/agentNameInfo'
import AgentPhoneInfo from '~/features/agents/components/agentInfoHeader/agentPhoneInfo'
import useGetAgentCentralized from '~/features/agents/hooks/useGetAgentCentralized'
import useGetElementDimensions from '~/features/ui/hooks/useGetElementDimensions'
import EElement from '~/features/ui/models'
import { scenariosSideNavWidthInPixels } from '~/layouts/account/scenariosLayout/constants'
import { EClientPath } from '~/models/api'
import EEnvironmentVariables from '~/models/config'
import { store } from '~/store'
import rem from '~/utils/ui'

function AgentInfoHeader() {
  const theme = useTheme()
  const { pathname } = useLocation()
  const { height: headerHeight } = useGetElementDimensions(
    EElement.accountHeader,
  )
  const { isLoading, isError } = useGetAgentCentralized()

  const [isExpanded, setIsExpanded] = useState(false)
  const tier = store((state) => state.organization.paymentTier)

  const isScenariosPage =
    pathname.includes(EClientPath.scenariosBase) &&
    import.meta.env[EEnvironmentVariables.VITE_IS_SCENARIOS_ENABLED] === 'true'

  if (isLoading) {
    return (
      <LoadingPaperSkeleton
        sx={{
          position: 'sticky',
          height: { xxxs: rem(177), lg: rem(92) },
          backgroundColor: theme.palette.mainBackground,
        }}
      />
    )
  }

  if (isError) {
    return null
  }

  return (
    <Paper
      elevation={3}
      sx={{
        padding: {
          xxxs: `${rem(20)} ${rem(16)}`,
          sm: `${rem(20)} ${rem(24)}`,
          lg: `${rem(20)} ${rem(28)} ${rem(9)} ${rem(28)}`,
        },
        backgroundColor: theme.palette.mainBackground,
        width: '100%',
        position: 'sticky',
        top: rem(headerHeight ?? 0),
        zIndex: 2,
        ...(isScenariosPage
          ? {
              width: { md: `calc(100% - ${scenariosSideNavWidthInPixels}px)` },
              marginLeft: { md: rem(scenariosSideNavWidthInPixels) },
            }
          : {}),
      }}
    >
      <Stack
        direction={{ xxxs: 'column', lg: 'row' }}
        sx={{ columnGap: { xxxs: 1, lg: 11 }, rowGap: 1, flexWrap: 'wrap' }}
      >
        <Box
          sx={{
            display: 'flex',
            width: { xxxs: '100%', lg: 'auto' },
            justifyContent: 'space-between',
          }}
        >
          <AgentNameInfo />

          <IconButton
            onClick={() => setIsExpanded((prev) => !prev)}
            sx={{
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s',
              marginBottom: 'auto',
              display: { xxxs: 'flex', lg: 'none' },
            }}
          >
            <ExpandMore />
          </IconButton>
        </Box>

        <Stack
          direction={{ xxxs: 'column', lg: 'row' }}
          spacing={{ xxxs: isExpanded ? 2 : 1, lg: 6 }}
        >
          <AgentPhoneInfo isExpanded={isExpanded} tier={tier} />
          <AgentEmailInfo isExpanded={isExpanded} tier={tier} />
          <AgentChatInfo isExpanded={isExpanded} />
        </Stack>
      </Stack>
    </Paper>
  )
}

export default AgentInfoHeader
